import { Bookmark, DetailSession, Recording } from '@streem/sdk-core';
import { useObservable } from '@streem/sdk-react';
import { Column, styled } from '@streem/ui-react';

import { useGlobalStore } from '../../hooks/use_global_context';
import { isRecordingPending } from '../../util/artifacts';
import { AddNewBookmark } from '../call_details/tabs/summary/add_new_bookmark';
import { MediaGalleryBookmark } from './bookmark';
import { MAX_VIDEO_SECTION_HEIGHT, MAX_VIDEO_WIDTH, MediaGalleryHeader } from './shared.styles';

interface BookmarksProps {
    detailSession: DetailSession;
    recordings: Recording[];
    bookmarks: Bookmark[];
    readOnly: boolean;
}

export function Bookmarks({
    detailSession,
    recordings,
    readOnly,
    bookmarks,
}: BookmarksProps): JSX.Element {
    const someRecordingsPending = isRecordingPending(recordings);
    const [callStartTime] = useObservable(detailSession.bookmarks.callStartTime);
    const { authStore } = useGlobalStore();

    return (
        <BookmarkSection>
            <MediaGalleryHeader>Bookmarks</MediaGalleryHeader>

            <BookmarkOutline>
                {callStartTime && !someRecordingsPending && !readOnly && (
                    <AddNewBookmark
                        addBookmark={bookmark => detailSession.bookmarks.addBookmark(bookmark)}
                    />
                )}
                <BookmarkContainer>
                    {bookmarks.length > 0 &&
                        callStartTime &&
                        bookmarks
                            .sort((a, b) => {
                                const aCreatedAtTime =
                                    a.bookmark.timestamp?.seconds.low +
                                    a.bookmark.timestamp?.nanos / 1000000000;
                                const bCreatedAtTime =
                                    b.bookmark.timestamp?.seconds.low +
                                    b.bookmark.timestamp?.nanos / 1000000000;
                                if (aCreatedAtTime > bCreatedAtTime) {
                                    return 1;
                                } else {
                                    return -1;
                                }
                            })
                            .map(bookmark => (
                                <MediaGalleryBookmark
                                    enableToolTip={someRecordingsPending}
                                    disableTimestamp={someRecordingsPending}
                                    key={bookmark.id}
                                    bookmarkArtifact={bookmark}
                                    isBookmarkOwner={bookmark.ownerUserId === authStore.userId}
                                    updateBookmark={(label: string) => {
                                        const newBookmark = Object.assign(bookmark.bookmark);
                                        detailSession.bookmarks.updateBookmark(bookmark.id, {
                                            ...newBookmark,
                                            label,
                                        });
                                    }}
                                    deleteBookmark={async () => {
                                        await detailSession.bookmarks.deleteBookmark(bookmark.id);
                                    }}
                                />
                            ))}
                </BookmarkContainer>
            </BookmarkOutline>
        </BookmarkSection>
    );
}

const BookmarkSection = styled.div`
    position: relative;
    display: flex;
    flex: 0 1 25rem;
    min-width: 18.4rem;
    flex-direction: column;
    margin-bottom: 0.125rem;
    max-height: ${MAX_VIDEO_SECTION_HEIGHT};
    max-width: ${MAX_VIDEO_WIDTH};
    min-height: 10rem;
    @media (max-width: 1075px) {
        margin-top: 16px;
        flex: 1 auto;
        max-width: 100%;
        max-height: 25rem;
        min-height: 10rem;
    }
`;

const BookmarkContainer = styled.div`
    overflow: auto;
    padding: 0 1rem 0 1rem;
    width: 100%;
    @media (max-width: 1075px) {
        padding: 0px;
    }
`;

const BookmarkOutline = styled(Column)(({ theme }) => ({
    border: `0.063rem ${theme.colors.grey10} solid`,
    borderRadius: '.5rem',
    minHeight: '10rem',
}));
